import * as React from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Box, useTheme, useMediaQuery, IconButton, Grid, List } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOut, faBars, faTimes } from '@fortawesome/pro-regular-svg-icons';

import { NavItems } from './NavItems';
import { AxiosUtils, PlatformUtils } from '../../utils';
import { CerebroHeading, CerebroIcon, CerebroStyledBox, CerebroText, CerebroTextButton } from '..';
import { fontFamily, useApi, useAuth, useCerebroNav, weights } from '../../context';
import { CerebroNavigationDrawer } from './CerebroNavigationDrawer';
import { PlatformRootUrl } from '../../utils/platform';
import { StatusBar } from './StatusBar';
import {
	useDataRoomAccessQuery,
	useDataRoomQuery,
	useDataRoomTeamMemberQuery,
	useDealQuery,
	useLenderDataRoomUserQuery,
} from '../../hooks/Queries';
import { LenderMilestoneDetails } from './LenderMilestoneDetails';
import { ActiveDataRoomUser, CurrentUser, DataRoomConfirmation, Deal } from '../../models';
import { LenderRequestCallForm } from './LenderRequestCallForm';
import { LenderOptOutForm } from './LenderOptOutForm';
import { MilestoneReport } from '../../models/MilestoneReport';
import { LaunchDealForm } from './LaunchDealForm';
import { AccessNdaRequestDialog } from '../../../pages/Nda/AccessNdaRequestDialog';
import { getErrorStatus } from '../../utils/axios';
import { useDataRoomSessionQuery } from '../../hooks/Queries/useDataRoomSessionQuery';
import { CerebroApiEndpoints } from '../../utils/api';
import { setCookie } from '../../utils/cookie';
import { UseQueryResult } from 'react-query';

export const Layout: React.FC = () => {
	const theme = useTheme();
	const { currentUser } = useAuth();
	const api = useApi();
	const isLender = currentUser.isBankUser || currentUser.isTeamMemberUser;
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [milestoneDetail, setMilestoneDetail] = React.useState<MilestoneReport | null | undefined>(null);
	const [lenderCall, setLenderCall] = React.useState<ActiveDataRoomUser | null | undefined>(null);
	const [lenderOptOut, setLenderOptOut] = React.useState<ActiveDataRoomUser | null | undefined>(null);
	const [launchConfirmation, setLaunchConfirmation] = React.useState<DataRoomConfirmation | null | undefined>(null);

	const onCloseDrawersAndDialogs = () => {
		setMilestoneDetail(null);
		setLenderCall(null);
		setLenderOptOut(null);
		setLaunchConfirmation(null);
		setLenderPopupOpen(false);
	};

	const onLenderOptOut = () => {
		setLenderOptOut(null);
		window.location.href = `${PlatformRootUrl}/Home/MyDashboard`;
	};

	const onDeclineNda = () => {
		window.location.href = `${PlatformRootUrl}/Home/MyDashboard`;
	};

	const cerebroNav = useCerebroNav();
	const [mobileNavOpen, setMobileNavBarOpen] = React.useState(false);

	const [lenderPopupOpen, setLenderPopupOpen] = React.useState(isLender);
	const [ndaDialogOpen, setNdaDialogOpen] = React.useState(isLender);

	const location = useLocation();

	React.useEffect(() => {
		if (!isMobile && mobileNavOpen) setMobileNavBarOpen(false);
	}, [isMobile, mobileNavOpen]);

	React.useEffect(() => {
		// Close the mobile nav bar whenever the navigation changes
		if (mobileNavOpen) setMobileNavBarOpen(false);
	}, [location]); // eslint-disable-line react-hooks/exhaustive-deps

	const { guid } = useParams<'guid'>();
	const dataRoomQuery = useDataRoomQuery(guid);
	const dealQuery = useDealQuery(dataRoomQuery.data?.dealGuid, {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});

	const roomUserQuery = useLenderDataRoomUserQuery(guid, currentUser.userGuid, {
		enabled: isLender && !currentUser.isTeamMemberUser,
		retry: false,
	});

	const accessQuery = useDataRoomAccessQuery(guid, {
		enabled: isLender,
		retry: false,
	});

	const teamMemberQuery = useDataRoomTeamMemberQuery(guid, currentUser.userGuid, {
		enabled: isLender && currentUser.isTeamMemberUser,
		retry: false,
	});

	const hasDataRoomSession = useDataRoomSessionQuery(guid, currentUser.userGuid);

	const createAccessLog = async () => {
		setCookie('DataRoomSession', currentUser.userGuid);
		await AxiosUtils.getResponseData(api.post(CerebroApiEndpoints.DataRooms.CreateDataRoomAccessedLog(guid)));
	};

	if (!hasDataRoomSession) createAccessLog();

	if (!guid || (dataRoomQuery.error && getErrorStatus(dataRoomQuery.error) == 401)) {
		window.location.href = PlatformRootUrl;
		return null;
	}

	if (rawLeadRedirect(dealQuery, currentUser)) {
		window.location.href = `${PlatformRootUrl}/Home/MyDashboard`;
		return null;
	}

	function getPageHeader(data: Deal, required: boolean, accepted: boolean) {
		const fullHeader = !!data.dealName ? `${data.companyName} - ${data.dealName}` : `${data.companyName}`;

		if (required) return accepted ? fullHeader : data.dealName;

		return fullHeader;
	}

	return (
		<Box sx={{ overflowX: 'hidden' }}>
			<AppBar
				position="relative"
				sx={theme => ({
					backgroundColor: theme.palette.gray.background,
					height: 64,
					justifyContent: 'center',
					zIndex: 1000,
				})}
			>
				<Grid container component={Toolbar} justifyContent={isMobile ? 'center' : 'space-between'}>
					<Grid item xs={12} md="auto">
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							{isMobile && (
								<IconButton onClick={() => setMobileNavBarOpen(!mobileNavOpen)} sx={{ marginRight: 2 }}>
									<CerebroIcon sx={theme => ({ color: theme.palette.gray.dark })}>
										<FontAwesomeIcon icon={faBars} size="lg" />
									</CerebroIcon>
								</IconButton>
							)}
							<a href={PlatformRootUrl} style={{ lineHeight: 0 }}>
								<img src={`${process.env.PUBLIC_URL}/Cerebro-Capital-Logo.jpg`} width="210"></img>
							</a>
						</Box>
					</Grid>
					{!isMobile && (
						<>
							<Grid item>
								<Typography
									variant="h3"
									component="div"
									sx={theme => ({
										color: theme.palette.gray.dark,

										fontFamily,
										fontWeight: weights.semibold,
										fontSize: 22,

										lineHeight: '27px',

										textAlign: 'center',
									})}
								>
									{dataRoomQuery.isSuccess &&
									dealQuery.isSuccess &&
									(!isLender || (isLender && accessQuery.isSuccess && !!accessQuery.data)) ? (
										<>
											DATA ROOM
											<br></br>
											<CerebroHeading size="h3">
												{getPageHeader(dealQuery.data, isLender, accessQuery.data ?? false)}
											</CerebroHeading>
										</>
									) : (
										<>DATA ROOM</>
									)}
								</Typography>
							</Grid>
							<Grid item>
								<CerebroTextButton href={PlatformUtils.GetLogoutUrl()}>
									Logout
									<CerebroIcon padLeft sx={theme => ({ color: theme.palette.primary.main })}>
										<FontAwesomeIcon icon={faSignOut} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
							</Grid>
						</>
					)}
				</Grid>
			</AppBar>
			<Box sx={{ display: isMobile ? 'block' : 'flex', minHeight: `calc(100vh - 64px)` }}>
				<CerebroNavigationDrawer open={mobileNavOpen} onClose={() => setMobileNavBarOpen(false)}>
					{isMobile && (
						<Grid
							container
							component={Toolbar}
							justifyContent="space-between"
							alignItems="center"
							sx={{ boxShadow: 3 }}
						>
							<Grid item>
								<IconButton onClick={() => setMobileNavBarOpen(!mobileNavOpen)} sx={{ marginRight: 2 }}>
									<CerebroIcon sx={theme => ({ color: theme.palette.gray.dark })}>
										<FontAwesomeIcon fixedWidth icon={faTimes} size="lg" />
									</CerebroIcon>
								</IconButton>
							</Grid>

							<Grid item>
								<CerebroTextButton href={PlatformUtils.GetLogoutUrl()}>
									Logout
									<CerebroIcon padLeft sx={theme => ({ color: theme.palette.primary.main })}>
										<FontAwesomeIcon icon={faSignOut} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
							</Grid>
						</Grid>
					)}
					<List sx={{ width: 270 }} dense>
						<NavItems level={1} navItems={cerebroNav.navItems} />
					</List>
				</CerebroNavigationDrawer>
				<Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
					<StatusBar
						currentUser={currentUser}
						isDirectIntroDeal={dataRoomQuery.data?.isDirectIntroDeal ?? false}
						viewMilestoneDetail={setMilestoneDetail}
						lenderCallRequest={setLenderCall}
						lenderOptOut={setLenderOptOut}
						launchDeal={setLaunchConfirmation}
					/>

					{currentUser.isBankUser && (
						<>
							<LenderMilestoneDetails report={milestoneDetail} close={onCloseDrawersAndDialogs} />
							<LenderRequestCallForm
								dataRoomGuid={guid}
								activeUser={lenderCall}
								close={onCloseDrawersAndDialogs}
							/>
							<LenderOptOutForm
								dataRoomGuid={guid}
								user={lenderOptOut}
								optedOut={onLenderOptOut}
								close={onCloseDrawersAndDialogs}
							/>
						</>
					)}
					<LaunchDealForm
						currentUser={currentUser}
						dataRoomGuid={guid}
						deal={dealQuery.data}
						confirmation={launchConfirmation}
						close={onCloseDrawersAndDialogs}
					/>

					{!isLender && <Outlet />}

					{isLender && accessQuery.isSuccess && accessQuery.data && <Outlet />}

					{isLender && accessQuery.isSuccess && !accessQuery.data && (
						<AccessNdaRequestDialog
							open={ndaDialogOpen}
							dataRoomGuid={guid}
							roomUserGuid={
								currentUser.isTeamMemberUser
									? teamMemberQuery.data?.bankContactTeamMemberGuid
									: roomUserQuery.data?.guid
							}
							accept={() => window.location.reload()}
							close={onDeclineNda}
						/>
					)}

					{isLender && accessQuery.isError && (
						<CerebroStyledBox>
							<CerebroText>
								Whoops, something went wrong. Please refresh the page or try again later.
							</CerebroText>
						</CerebroStyledBox>
					)}
				</Box>
			</Box>
		</Box>
	);
};

function rawLeadRedirect(dealQuery: UseQueryResult<Deal, unknown>, currentUser: CurrentUser): boolean {
	if (!dealQuery.isSuccess) {
		return false;
	}

	if (dealQuery.data.isRawLead && !currentUser.isEmployeeUser) {
		return true;
	}

	if (dealQuery.data.isRawLead && !dealQuery.data.isRawLeadVisible) {
		return true;
	}

	return false;
}
